import { useEffect, useState } from 'react'
import { useFieldArray, useFormContext } from 'react-hook-form'
import {
  Box as MuiBox,
  formControlClasses,
  formGroupClasses,
  FormLabel,
  SxProps,
} from '@mui/material'

import { DefaultElementProps, Element } from 'common/interfaces'
import { fieldsetBorder } from 'common/styles'
import DeleteAppendedItemModal from 'components/Modals/DeleteAppendedItemModal'
import { Box } from 'elements/Box'
import { useBoolean } from 'utils/hook'
import { concatToSXProp } from 'utils/style'

import AddAnotherButton from './components/AddAnotherButton'
import RemoveButton from './components/RemoveButton'
import { Append as AppendProps } from './interface'

function recursivelyAddPrefixToElements(elements: Element[], prefix: string): Element[] {
  if (elements === undefined) {
    return []
  }

  return elements.map((element) => {
    const prefixedElement: Element = {
      ...element,
      name: `${prefix}.${element.name}`,
    }

    if (element.type !== Append.name) {
      prefixedElement.children = recursivelyAddPrefixToElements(element.children, prefix)
    }

    return prefixedElement
  })
}

const styles: Record<string, SxProps> = {
  box: {
    position: 'relative',
    width: '100%',
    ':not(:last-child)': {
      borderBottom: '1px solid #0000001F',
      marginBottom: '24px',
      paddingBottom: '24px',
    },
    [`& .${formGroupClasses.root} > div`]: {
      flex: 1,
      '& > .sizing-container': {
        width: '100%',
      },
    },
  },

  formLabel: {
    display: 'inline-block',
    mb: '12px',
  },
}

export default function Append({
  name,
  label,
  border = true,
  direction = 'row',
  children,
  deletingWord = 'item',
  background,
}: AppendProps & DefaultElementProps): JSX.Element | null {
  const [isModalOpen, openModal, closeModal] = useBoolean(false)

  const [selectedIndex, setSelectedIndex] = useState(0)

  const { control } = useFormContext()
  const { fields, remove, append, replace } = useFieldArray({
    control,
    name,
  })

  const openDeleteModalForOneItem = (index: number): void => {
    openModal()
    setSelectedIndex(index)
  }

  const deleteSelectedItem = (): void => {
    closeModal()
    remove(selectedIndex)
  }

  // this effect is stands for having at least
  // one group of children after mounting
  useEffect(() => {
    if (fields.length === 0) {
      replace([{}])
    }
  }, [replace])

  if (children === undefined) {
    return null
  }

  const fieldsCount = fields.length

  const mappedChildren = fields.map((item, index) => {
    const elements = recursivelyAddPrefixToElements(children, `${name}.${index}`)

    return (
      <MuiBox
        className="append-input-wrap"
        sx={concatToSXProp(styles.box, {
          ...(!label && {
            '&:first-child': {
              paddingTop: '24px',
              borderTop: '1px solid #0000001F',
            },
          }),
        })}
        key={item.id}
      >
        {label !== undefined && (
          <FormLabel sx={border ? {} : styles.formLabel}>
            {label}
            {fieldsCount > 1 && ` #${index + 1}`}
          </FormLabel>
        )}
        <MuiBox
          sx={{
            display: 'flex',
            alignItems: 'start',
            background: background,
            ...(border && {
              ...fieldsetBorder,
              [`& > .${formControlClasses.root}`]: {
                border: 'none',
              },
            }),
          }}
        >
          <Box
            key={item.id}
            direction={direction}
            border={border}
            validations={{}}
            name={`${name}-box-${item.id}`}
          >
            {elements}
          </Box>
          <RemoveButton
            onClick={() => openDeleteModalForOneItem(index)}
            border={border}
            sx={{
              right: border ? '-17px' : '-8px',
              top: label !== undefined ? '-6px' : '-17px',
            }}
          />
        </MuiBox>
      </MuiBox>
    )
  })

  return (
    <MuiBox className="append-wrap">
      {mappedChildren}
      <AddAnotherButton onClick={() => append({})} text={deletingWord} />
      {isModalOpen && (
        <DeleteAppendedItemModal
          open={isModalOpen}
          onClose={closeModal}
          onDelete={deleteSelectedItem}
          title={deletingWord}
        />
      )}
    </MuiBox>
  )
}
